import type { IconName } from "components/Icon";

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { TransientResultFile } from "../lib/formatJobResults";

import { useResultSelection } from "../hooks/useResultSelection";
import { useResultZipDownload } from "../hooks/useResultZipDownload";
import { useTransientJobResults } from "../hooks/useTransientJobResults";
import { RESULT_KIND, type ResultKind } from "../model/constants";
import { TransientJobDownloadModal } from "./TransientJobDownloadModal";
import { TransientJobResultIterationGroup } from "./TransientJobResultIterationGroup";
import { TransientJobResultToolbar } from "./TransientJobResultToolbar";

type TransientJobResultsProps = {
  configId?: number;
  shouldRefresh: boolean;
};

const JSON_ICON_COLOR = "var(--color-purpose-certificate-gold)";
const XLSX_ICON_COLOR = "var(--color-purpose-status-positive)";

const dictionaryIcon = (): { color: string; iconName: IconName } => ({
  color: JSON_ICON_COLOR,
  iconName: "Json",
});

const hourlyIcon = (_file: TransientResultFile): { color: string; iconName: IconName } => ({
  color: XLSX_ICON_COLOR,
  iconName: "Xlsx",
});

export const TransientJobResults = ({ configId, shouldRefresh }: TransientJobResultsProps) => {
  const { t } = useTranslation();
  const { dictionaries, error, hourly, isLoading } = useTransientJobResults(configId, shouldRefresh);
  const [kind, setKind] = useState<ResultKind>(RESULT_KIND.hourly);
  const folders = kind === RESULT_KIND.hourly ? hourly : dictionaries;
  const { activeKey, activeScenario, isSelected, scenarios, selectedFiles, setActiveKey, setMany, toggle } =
    useResultSelection(folders);
  const download = useResultZipDownload();
  const [roomSearch, setRoomSearch] = useState("");
  const zipName = useMemo(() => {
    const suffix = kind === RESULT_KIND.hourly ? "room-hourly" : "room-dictionaries";
    return `${activeKey || "rooms"}-${suffix}.zip`;
  }, [activeKey, kind]);

  if (isLoading) {
    return <p className="text-lds-foreground-muted font-ds-body-regular-md">{t("general.loadingDataPleaseWait")}</p>;
  }
  if (error) {
    return <p className="text-lds-purpose-status-negative-subtle">{error}</p>;
  }
  if (dictionaries.length === 0 && hourly.length === 0) {
    return (
      <p className="text-lds-foreground-muted font-ds-body-regular-md">
        {t("Execution.transient.jobDetails.resultsEmpty")}
      </p>
    );
  }

  return (
    <div>
      <TransientJobResultToolbar
        activeKey={activeKey}
        activeKind={kind}
        disabled={selectedFiles.length === 0}
        onDownload={() => download.start(selectedFiles, activeKey, zipName)}
        onSearch={setRoomSearch}
        onSelectKind={(nextKind) => {
          setRoomSearch("");
          setKind(nextKind);
        }}
        onSelectScenario={(key) => {
          setRoomSearch("");
          setActiveKey(key);
        }}
        scenarios={scenarios}
        search={roomSearch}
      />
      {activeScenario ? (
        <div className="grid grid-cols-3 gap-4">
          {activeScenario.iterations.map((iteration) => (
            <TransientJobResultIterationGroup
              fileIcon={kind === RESULT_KIND.hourly ? hourlyIcon : dictionaryIcon}
              isSelected={isSelected}
              iteration={iteration}
              key={iteration.folderName}
              onSetMany={setMany}
              onToggle={toggle}
              search={roomSearch}
            />
          ))}
        </div>
      ) : (
        <p className="text-lds-foreground-muted font-ds-body-regular-md">
          {t("Execution.transient.jobDetails.resultsEmpty")}
        </p>
      )}
      <TransientJobDownloadModal
        error={download.error}
        isRunning={download.isRunning}
        onClose={download.close}
        open={download.open}
        progress={download.progress}
      />
    </div>
  );
};
